import React from 'react';
import { View } from 'react-native';

import Text from '@/components/Typography/Text';
import Reveal from '@/components/Landing/Reveal';
import {
  Figure,
  GhostCta,
  PrimaryCta,
  Section,
  useLanding,
} from '@/components/Landing/LandingKit';
import { CheckLine, MicroLabel } from '@/components/Landing/MarketingKit';
import { fluid } from '@/components/Landing/landingTokens';
import { BILLING, PREMIUM_BILLING, PLANS, PLUS_FIGURES } from '@/constants/pricingContent';

/**
 * One plan column.
 *
 * The highlighted plan gets the accent wash and a solid CTA; the others stay on
 * the page surface with hairline borders, so the eye lands on Plus without the
 * comparison turning into three competing cards.
 */
function PlanColumn({ plan, price, cadence, isAnnual, onSelect }) {
  const { c, isCompact, isXS } = useLanding();
  const featured = Boolean(plan.highlight);
  const billing = plan.id === 'plus' ? BILLING : PREMIUM_BILLING;
  const billingNote = plan.id === 'free'
    ? plan.note
    : `${plan.id === 'plus-premium' ? 'Web only · ' : ''}${isAnnual ? `$${billing.annualTotal} billed annually` : 'Billed monthly'}`;

  return (
    <View
      style={{
        // `flexBasis` sizes the main axis, which is the height once the row
        // stacks into a column — a percentage there resolves against the
        // container's own content height and collapses the stack.
        flexGrow: isCompact ? 0 : 1,
        flexBasis: isCompact ? 'auto' : 0,
        minWidth: 0,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: featured ? c.accentRing : c.rule,
        backgroundColor: featured ? c.accentWash : c.cardSolid,
        padding: isXS ? 22 : 28,
        gap: 20,
      }}
    >
      <View style={{ gap: 8, minHeight: isCompact ? undefined : 84 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Text tag="h2" weight="semibold" style={{ color: c.ink, fontSize: 22, letterSpacing: -0.3 }}>
            {plan.name}
          </Text>
          {featured ? (
            <View
              style={{
                paddingHorizontal: 8,
                paddingVertical: 3,
                borderRadius: 4,
                backgroundColor: c.accentFill,
              }}
            >
              <Text weight="bold" style={{ color: '#FFFFFF', fontSize: 9.5, letterSpacing: 0.3 }}>
                MOST POPULAR
              </Text>
            </View>
          ) : null}
          {!featured && plan.badge ? (
            <View
              style={{
                paddingHorizontal: 8,
                paddingVertical: 3,
                borderRadius: 4,
                backgroundColor: c.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
              }}
            >
              <Text weight="bold" style={{ color: c.muted, fontSize: 9.5, letterSpacing: 0.2 }}>
                {plan.badge.toUpperCase()}
              </Text>
            </View>
          ) : null}
        </View>
        <Text style={{ color: c.muted, fontSize: 14, lineHeight: 21 }}>{plan.tagline}</Text>
      </View>

      <View>
        <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 8 }}>
          <Text
            weight="bold"
            style={{
              color: c.ink,
              fontSize: 42,
              lineHeight: 48,
              letterSpacing: -2,
              fontVariant: ['tabular-nums'],
            }}
          >
            {price}
          </Text>
          <Text weight="medium" style={{ color: c.muted, fontSize: 13.5 }}>{cadence}</Text>
        </View>
        <Text style={{ color: c.muted, fontSize: 12.5, lineHeight: 20, marginTop: 8 }}>{billingNote}</Text>
      </View>

      {featured ? (
        <PrimaryCta restrained label={plan.cta} href={plan.href} onPress={onSelect} full />
      ) : (
        <GhostCta label={plan.cta} href={onSelect ? undefined : plan.href} onPress={onSelect} style={{ alignSelf: 'stretch', alignItems: 'center', borderWidth: 1, borderColor: c.ruleStrong, borderRadius: 6 }} />
      )}

      <View style={{ height: 1, backgroundColor: c.rule }} />

      <View style={{ gap: 11 }}>
        <MicroLabel>What&apos;s included</MicroLabel>
        {plan.features.map((feature) => (
          <CheckLine key={feature.name} included={feature.included}>
            {feature.name}
          </CheckLine>
        ))}
      </View>
    </View>
  );
}

/** The plan comparison, then the numbers a Plus subscription buys. */
function PricingPlans({ isAnnual, onSelectPlan }) {
  const { c, isCompact, isXS, width } = useLanding();
  const enterprise = PLANS.find((plan) => plan.id === 'enterprise');

  const priceFor = (plan) => {
    if (plan.id === 'plus') {
      return `$${isAnnual ? BILLING.annualPerMonth : BILLING.monthly}`;
    }
    if (plan.id === 'plus-premium') {
      return `$${isAnnual ? PREMIUM_BILLING.annualPerMonth : PREMIUM_BILLING.monthly}`;
    }
    return plan.price;
  };

  return (
    <Section label="Plans" top={false} contentStyle={{ paddingBottom: fluid(width, 44, 72) }}>
      <Reveal>
        <View
          style={{
            flexDirection: isCompact ? 'column' : 'row',
            gap: isXS ? 14 : 18,
            alignItems: 'stretch',
          }}
        >
          {PLANS.filter((plan) => plan.id !== 'enterprise').map((plan) => (
            <PlanColumn
              key={plan.id}
              plan={plan}
              isAnnual={isAnnual}
              price={priceFor(plan)}
              cadence={plan.id === 'plus' || plan.id === 'plus-premium' ? 'per month' : plan.cadence}
              onSelect={onSelectPlan ? () => onSelectPlan(plan) : undefined}
            />
          ))}
        </View>
      </Reveal>

      {isAnnual ? (
        <Reveal delay={80}>
          <Text
            style={{
              color: c.muted,
              fontSize: 12.5,
              lineHeight: 20,
              marginTop: 18,
              textAlign: isCompact ? 'left' : 'center',
            }}
          >
            Annual Plus is ${BILLING.annualTotal}/yr ({BILLING.savingsPercent}% off). Plus Premium
            annual is ${PREMIUM_BILLING.annualTotal}/yr ({PREMIUM_BILLING.savingsPercent}% off).
          </Text>
        </Reveal>
      ) : null}

      <Reveal delay={80}>
        <View style={{ marginTop: 24, borderWidth: 1, borderColor: c.rule, borderRadius: 10, padding: isXS ? 22 : 28, backgroundColor: c.panel }}>
          <View style={{ flexDirection: isCompact ? 'column' : 'row', alignItems: isCompact ? 'flex-start' : 'center', justifyContent: 'space-between', gap: 20 }}>
            <View style={{ flex: isCompact ? undefined : 1, gap: 8 }}>
              <Text tag="h2" weight="semibold" style={{ color: c.ink, fontSize: 22, letterSpacing: -0.4 }}>{enterprise.name}</Text>
              <Text style={{ color: c.inkSoft, fontSize: 14, lineHeight: 22 }}>{enterprise.tagline}</Text>
              <Text style={{ color: c.muted, fontSize: 12.5, lineHeight: 20 }}>{enterprise.price} pricing · {enterprise.note}</Text>
            </View>
            <GhostCta label={enterprise.cta} href={onSelectPlan ? undefined : enterprise.href} onPress={onSelectPlan ? () => onSelectPlan(enterprise) : undefined} />
          </View>
          <View style={{ marginTop: 24, paddingTop: 22, borderTopWidth: 1, borderTopColor: c.rule, flexDirection: isCompact ? 'column' : 'row', flexWrap: 'wrap', rowGap: 12, columnGap: 24 }}>
            {enterprise.features.map((feature) => (
              <View key={feature.name} style={{ width: isCompact ? '100%' : '30%' }}>
                <CheckLine>{feature.name}</CheckLine>
              </View>
            ))}
          </View>
        </View>
      </Reveal>

      {/* The subscription restated as scope rather than features. */}
      <Reveal delay={100}>
        <View
          style={{
            marginTop: fluid(width, 32, 44),
            flexDirection: isCompact ? 'column' : 'row',
            borderTopWidth: 1,
            borderBottomWidth: 1,
            borderColor: c.rule,
          }}
        >
          {PLUS_FIGURES.map((figure, index) => (
            <View
              key={figure.label}
              style={{
                flex: isCompact ? undefined : 1,
                paddingVertical: isXS ? 22 : 32,
                paddingRight: isCompact ? 0 : 24,
                paddingLeft: !isCompact && index > 0 ? 24 : 0,
                borderLeftWidth: !isCompact && index > 0 ? 1 : 0,
                borderTopWidth: isCompact && index > 0 ? 1 : 0,
                borderColor: c.rule,
              }}
            >
              <Figure value={figure.value} label={figure.label} size="sm" accent={index === 0} />
            </View>
          ))}
        </View>
      </Reveal>
    </Section>
  );
}

export default React.memo(PricingPlans);
